/**
 * Area Page Observability Service
 *
 * Tracks area page load events for critical city monitoring.
 * Integrates with NewRelic Browser APM for real-time alerting.
 */

import { trackEvent } from '../utils/event-tracker';
import {
  AREA_PAGE_LOAD_SUCCESS,
  AREA_PAGE_CRITICAL_CITY_ZERO_LISTINGS,
  AREA_PAGE_OBSERVABILITY_DEFAULTS,
} from '../constants/area-page-events';

/**
 * Area page type for tracking
 */
export type AreaPageType = 'standard' | 'hybrid';

/**
 * Parameters for tracking area page load success
 */
export interface AreaPageLoadSuccessParams {
  /** City/area slug (e.g., 'toronto-on', 'vancouver-bc') */
  citySlug: string;

  /** Province or state (e.g., 'ON', 'BC', 'FL') */
  provinceOrState: string;

  /** Country code (e.g., 'CA', 'US') */
  country: string;

  /** Number of listings returned */
  listingCount: number;

  /** Whether this is a critical city (from tenant config) */
  isCriticalCity: boolean;

  /** Page load time in milliseconds */
  loadTimeMs: number;

  /** Page type: 'standard' or 'hybrid' */
  pageType: AreaPageType;

  /** Tenant ID from component context */
  tenantId: string;
}

/**
 * Area Page Observability Service
 *
 * Pure service with no React dependencies.
 * Called from React components/hooks for tracking.
 */
export const AreaPageObservabilityService = {
  /**
   * Track successful area page load
   *
   * For critical cities with zero listings, sends only the critical alert event.
   * For all other cases, sends the standard load success event.
   */
  trackLoadSuccess(params: AreaPageLoadSuccessParams): void {
    const {
      citySlug,
      provinceOrState,
      country,
      listingCount,
      isCriticalCity,
      loadTimeMs,
      pageType,
      tenantId,
    } = params;

    // Critical zero listings: send ONLY the high-priority alert (not success)
    if (isCriticalCity && listingCount === 0) {
      trackEvent(
        AREA_PAGE_CRITICAL_CITY_ZERO_LISTINGS,
        {
          citySlug,
          provinceOrState,
          country,
          loadTimeMs,
          pageType,
          alertPriority: AREA_PAGE_OBSERVABILITY_DEFAULTS.CRITICAL_ALERT_PRIORITY,
        },
        tenantId
      );
      return; // Don't send load_success for critical failures
    }

    // All other cases: track standard load success event
    trackEvent(
      AREA_PAGE_LOAD_SUCCESS,
      {
        citySlug,
        provinceOrState,
        country,
        listingCount,
        isCriticalCity,
        loadTimeMs,
        pageType,
      },
      tenantId
    );
  },
} as const;

